import { useCallback, useEffect, useState } from 'react';
import { APP_VERSION } from './appVersion';

const VERSION_STORAGE_KEY = 'appVersion';
const RELOAD_SESSION_KEY = 'tabulare_version_reload';
const VERSION_QUERY = '_v';
const CHECK_INTERVAL_MS = 60 * 1000;

async function fetchServerVersion() {
  const response = await fetch(`/version.json?t=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  });
  if (!response.ok) {
    throw new Error(`version.json ${response.status}`);
  }
  const data = await response.json();
  return data.version;
}

async function bustClientCaches() {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }
  if ('caches' in window) {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  }
}

function isSurveyRoute() {
  return window.location.pathname.startsWith('/responder-questionario');
}

function hardNavigate(serverVersion) {
  const url = new URL(window.location.href);
  url.searchParams.set(VERSION_QUERY, serverVersion);
  window.location.replace(url.toString());
}

function cleanVersionParam() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(VERSION_QUERY)) return;
  url.searchParams.delete(VERSION_QUERY);
  const query = url.searchParams.toString();
  window.history.replaceState({}, '', `${url.pathname}${query ? `?${query}` : ''}${url.hash}`);
}

const VersionChecker = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updating, setUpdating] = useState(false);

  const applyUpdate = useCallback(async (serverVersion, { force = false } = {}) => {
    if (!serverVersion) return;

    if (!force && isSurveyRoute()) {
      setUpdateAvailable(true);
      return;
    }

    if (!force && sessionStorage.getItem(RELOAD_SESSION_KEY) === serverVersion) {
      setUpdateAvailable(true);
      return;
    }

    sessionStorage.setItem(RELOAD_SESSION_KEY, serverVersion);
    setUpdating(true);
    await bustClientCaches();
    hardNavigate(serverVersion);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const serverVersion = await fetchServerVersion();
        if (cancelled || !serverVersion) return;

        if (serverVersion === APP_VERSION) {
          localStorage.setItem(VERSION_STORAGE_KEY, serverVersion);
          sessionStorage.removeItem(RELOAD_SESSION_KEY);
          cleanVersionParam();
          setUpdateAvailable(false);
          return;
        }

        await applyUpdate(serverVersion);
      } catch (error) {
        console.warn('Não foi possível verificar a versão do app:', error);
      }
    };

    check();
    const intervalId = setInterval(check, CHECK_INTERVAL_MS);
    const onFocus = () => {
      check();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [applyUpdate]);

  if (!updateAvailable) return null;

  return (
    <div className="app-update-banner" role="status">
      <span>Nova versão disponível. Atualize para ver as correções.</span>
      <button
        type="button"
        disabled={updating}
        onClick={async () => {
          const serverVersion = await fetchServerVersion().catch(() => APP_VERSION);
          await applyUpdate(serverVersion, { force: true });
        }}
      >
        {updating ? 'Atualizando...' : 'Atualizar agora'}
      </button>
    </div>
  );
};

export default VersionChecker;
